import { create } from "zustand"
import { persist } from "zustand/middleware"
import { produce } from "immer"

import { PropertySearchOutput, PropertySearchSchema } from "@/app/schema/property"
import { Category } from "@/app/types"
import { LocationData } from "./places"
import { getAmenitiesAction } from "@/actions/realstate/realstate.action"

// Define types for filters
interface Filters {
  category: Category | null
  location: LocationData | null
  minPrice: number | null
  maxPrice: number | null
  bedrooms: number | null
  bathrooms: number | null
  amenities: string[]
  keyword: Category | null
  sqft: number | null
}

// Default values for compact form
const COMPACT_DEFAULTS: Partial<Filters> = {
  category: null,
  location: null,
  minPrice: null,
  maxPrice: null,
  bedrooms: null,
  bathrooms: null,
}

// Default values for modal form
const MODAL_DEFAULTS: Partial<Filters> = {
  amenities: [],
  keyword: { id: "rent", name: "Rent" },
}

// Merged default filters
const DEFAULT_FILTERS: Filters = {
  ...COMPACT_DEFAULTS,
  ...MODAL_DEFAULTS,
} as Filters

// Validation helper
const validateFilters = (filters: Partial<Filters>, formType: "compact" | "modal") => {
  const validationFilters = {
    category: filters.category ?? null,
    location: filters.location ?? null,
    minPrice: filters.minPrice ?? null,
    maxPrice: filters.maxPrice ?? null,
    bedrooms: filters.bedrooms ?? null,
    bathrooms: filters.bathrooms ?? null,
    amenities: filters.amenities ?? [],
    keyword: filters.keyword ?? undefined,
    sqft: filters.sqft ?? undefined,
  }

  const result = PropertySearchSchema.safeParse(validationFilters)

  if (!result.success) {
    const errors = result.error.flatten().fieldErrors
    const formattedErrors = Object.entries(errors).reduce(
      (acc, [key, messages]) => {
        if (messages) acc[key] = messages.join(", ")
        return acc
      },
      {} as Record<string, string>
    )
    return { success: false, errors: formattedErrors }
  }

  return { success: true, data: result.data }
}

// Zustand store interface
interface PropertySearchState {
  filters: Filters
  activeForm: "compact" | "modal"
  amenities: { id: string; label: string }[]
  setFilter: <K extends keyof Filters>(key: K, value: Filters[K], formType?: "compact" | "modal") => void
  resetFilters: (formType?: "compact" | "modal") => void
  validateFilters: (formType?: "compact" | "modal") => {
    success: boolean
    errors?: Record<string, string>
    data?: PropertySearchOutput
  }
  loadAmenities: () => Promise<void>
}

export const usePropertySearchStore = create<PropertySearchState>()(
  persist(
    (set, get) => ({
      filters: DEFAULT_FILTERS,
      activeForm: "compact",
      amenities: [],

      setFilter: (key, value, formType) => {
        if (formType) {
          set({ activeForm: formType })
        }
        set(
          produce((state) => {
            state.filters[key] = value
          })
        )
      },

      resetFilters: (formType) => {
        set(
          produce((state) => {
            if (formType === "compact") {
              Object.assign(state.filters, { ...state.filters, ...COMPACT_DEFAULTS })
              state.activeForm = "compact"
            } else if (formType === "modal") {
              Object.assign(state.filters, { ...state.filters, ...MODAL_DEFAULTS })
              state.activeForm = "modal"
            } else {
              state.filters = DEFAULT_FILTERS
              state.activeForm = "compact"
            }
          })
        )
      },

      validateFilters: (formType = get().activeForm) => {
        const currentFilters = get().filters
        return validateFilters(currentFilters, formType)
      },

      loadAmenities: async () => {
        const currentAmenities = get().amenities
        if (currentAmenities.length > 0) return

        try {
          const response = await getAmenitiesAction()
          const loadedAmenities = (response.data?.data || []).map((item: any) => ({
            id: String(item.id || item._id),
            label: item.name || item.label,
          }))
          set({ amenities: loadedAmenities })
        } catch (error) {
          console.error("Failed to load amenities:", error)
        }
      },
    }),
    {
      name: "property-search-filters",
      partialize: (state) => ({
        filters: state.filters,
        activeForm: state.activeForm,
        amenities: state.amenities,
      }),
    }
  )
)
